import {RNG} from "../RNG";


export const SUSCEPTIBLE = 0;
export const INFECTED = 1;
export const REMOVED = 2;

export class GridNode {
  state: number;
  previousState: number;  // Used for doing one step of the simulation, then throw away

  infectionType: string|null;
  previousInfectionType: string|null;
  totalInfectionWeight: number;

  linked: boolean;
  mediaOutlet: boolean;
  specialDegree: number|null;
  realScientist: boolean;
  knowledgeLevel: number|null;

  rng: RNG;

  constructor(rng: RNG) {
    this.state = SUSCEPTIBLE;
    this.previousState = SUSCEPTIBLE;

    this.infectionType = null;
    this.previousInfectionType = null;
    this.totalInfectionWeight = 0;

    this.linked = false;
    this.mediaOutlet = false;
    this.specialDegree = null;
    this.realScientist = false;
    this.knowledgeLevel = null;

    this.rng = rng;
  }

  savePreviousState() {
    this.previousState = this.state;
    this.previousInfectionType = this.infectionType;
  }

  wasInfected(): boolean {
    return this.previousState === INFECTED;
  }

  isSusceptible(): boolean {
    return this.state === SUSCEPTIBLE;
  }

  isInfected(): boolean {
    return this.state === INFECTED;
  }

  isRemoved(): boolean {
    return this.state === REMOVED;
  }

  isAllowedToBeRemoved(): boolean {
    return !this.linked && this.knowledgeLevel === null;
  }

  setSusceptible() {
    this.state = SUSCEPTIBLE;
  }

  setInfected(infectionType: string, infectionWeight: number) {
    // if (infectionType === 'FAKE' && this.isRealScientist()) {
    //   // Rebuff the infection if it's the wrong type
    //   infectionWeight = 0;
    // }

    if (infectionType === '1' || infectionType === '2' || infectionType === '3' || infectionType === '4') {
      // We're modeling knowledge growth here...
      this.state = INFECTED;
      if (this.infectionType === null || parseInt(infectionType) > parseInt(this.infectionType)) {
        this.infectionType = infectionType;
      } else {
        // infection type stays the same
      }

      // maybe level up
      if (this.knowledgeLevel === 2 && this.infectionType === '1') {
        this.infectionType = '2';
      } else if (this.knowledgeLevel === 3 && this.infectionType === '2') {
        this.infectionType = '3';
      } else if (this.knowledgeLevel === 4 && this.infectionType === '3') {
        this.infectionType = '4';
      }
    } else {
      // Normal operations
      this.totalInfectionWeight += infectionWeight;

      // Only switch to the new infection if it crowds out the previous one
      if (this.rng.random() < infectionWeight / this.totalInfectionWeight) {
        this.state = INFECTED;
        this.infectionType = infectionType;
      }
    }
  }

  setRemoved() {
    this.state = REMOVED;
  }

  isMediaOutlet() {
    return this.mediaOutlet;
  }

  setMediaOutlet() {
    this.mediaOutlet = true;
  }

  isRealScientist() {
    return this.realScientist;
  }

  setRealScientist(realScientist: boolean) {
    this.realScientist = realScientist;

    this.specialDegree = 3;
  }

  getKnowledgeLevel(): number|null {
    return this.knowledgeLevel;
  }

  setKnowledgeLevel(knowledgeLevel: number) {
    this.knowledgeLevel = knowledgeLevel;
  }

  getKnowledgeLevelColor(): string {
    return ['#6BA306', '#19B655', '#1C94FF', '#BA5AFF'][this.knowledgeLevel-1];
  }

  getInfectionTypeColor(): string {
    return ['#6BA306', '#19B655', '#1C94FF', '#BA5AFF'][parseInt(this.infectionType)-1];
  }

  getInfectionType(): string {
    return this.infectionType;
  }

  resetInfectionWeight() {
    this.totalInfectionWeight = 0;
  }

  tryToInfect(neighbor: GridNode, transProb: number,
              modelScience: boolean, scientistTruthPref: number, careeristTruthPref: number) {
    if (neighbor.isRemoved()) {
      // Can't get infected if it's removed
      return;
    }

    // Test to see if neighbor should get active
    let infect = this.rng.random() < transProb;
    if (infect) {
      let infectionType = this.previousInfectionType;

      let actuallyInfect = true;
      if (modelScience) {
        let infectionWeight = null;
        if (this.isRealScientist()) {
          if (infectionType === 'REAL') {
            infectionWeight = scientistTruthPref;
          } else {
            infectionWeight = 1 - scientistTruthPref;
          }
        } else {
          if (infectionType === 'REAL') {
            infectionWeight = careeristTruthPref;
          } else {
            infectionWeight = 1 - careeristTruthPref;
          }
        }
        actuallyInfect = this.rng.random() < infectionWeight;
      }

      if (actuallyInfect) {
        neighbor.setInfected(infectionType, 1);
      }
    }
  }

  // deactivate() {
  //   this.activeColor = null;
  // }

  // getActivationColor() {
  //   return this.activeColor;
  // }

  // infect(targetNode: GridNode, rng: RNG) {
  //   // let mutationRate = 0.1;
  //   // let maxChange = 255 * mutationRate * mutationRate;
  //   //
  //   // let oldColor = Colors.makeHex(this.previousStepColor);
  //   //
  //   // let r = Colors.r(oldColor);
  //   // let g = Colors.g(oldColor);
  //   // let b = Colors.b(oldColor);
  //   // let newR = rng.randBetween(Math.max(r-maxChange, 0), Math.min(r+maxChange, 255));
  //   // let newG = rng.randBetween(Math.max(g-maxChange, 0), Math.min(g+maxChange, 255));
  //   // let newB = rng.randBetween(Math.max(b-maxChange, 0), Math.min(b+maxChange, 255));
  //   // let newColor = Colors.makeRGB(newR, newG, newB);
  //   //
  //   // targetNode.activeColor = Colors.hex(newColor);
  //   targetNode.activeColor = this.previousStepColor;
  // }
}


export class Node {
  network: any;
  neighbors: Set<Node>;
  infected: boolean;
  infectionHistory: boolean[];
  highlighted: boolean;
  patientZero: boolean;
  immune: boolean;

  centerX: number;
  centerY: number;

  constructor(network: any, centerX: number, centerY: number) {
    this.network = network;
    this.neighbors = new Set();
    this.infected = false;
    this.infectionHistory = [];
    this.highlighted = false;
    this.patientZero = false;
    this.immune = false;

    this.centerX = centerX;
    this.centerY = centerY;
  }

  addNeighbor(neighbor: Node) {
    this.neighbors.add(neighbor);
  }
}


export class Network {
  nodes: Set<Node>;

  constructor() {
    this.nodes = new Set();
  }

  addNode(node: Node) {
    if (node.network !== this) {
      throw new Error('Node being added to Network that it doesn\'t belong to.');
    }
    this.nodes.add(node);
  }

  getInfectedNodes(): Set<Node> {
    let infectedNodes = new Set();
    for (let node of this.nodes) {
      if (node.infected) {
        infectedNodes.add(node);
      }
    }
    return infectedNodes;
  }

  static generate(rng: RNG,
                  degree: number,
                  immunityFraction: number,
                  networkSize: number,
                  networkType: string): Network {
    let network = new Network();

    if (networkType === 'grid') {
      let numRows = Math.sqrt(networkSize);
      let numCols = Math.sqrt(networkSize);
      let gridHeight = 1.0 / numRows;
      let gridWidth = 1.0 / numCols;

      let nodes: Node[][] = [];

      for (let r = 0; r < numRows; r++) {
        let nodesForRow = [];
        for (let c = 0; c < numCols; c++) {
          let centerX = (c + 0.5) * gridWidth;
          let centerY = (r + 0.5) * gridHeight;

          let node = new Node(network, centerX, centerY);
          network.addNode(node);
          nodesForRow.push(node);

          // With some probability, make this node immune
          if (rng.random() < immunityFraction) {
            node.immune = true;
          }
        }
        nodes.push(nodesForRow);
      }

      // Infect patient zero
      let p0 = nodes[Math.floor(numRows/2)][Math.floor(numCols/2)];
      p0.patientZero = true;
      p0.infected = true;
      p0.immune = false;  // p0 can't be immune by definition

      // Fill in neighbor links
      for (let r = 0; r < numRows; r++) {
        for (let c = 0; c < numCols; c++) {
          let node = nodes[r][c];
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (dr === 0 && dc === 0) {
                continue;
              } else if (degree === 4 && dr !== 0 && dc !== 0) {
                continue;
              }

              let cr = r + dr;
              let cc = c + dc;
              if (cr >= 0 && cr < numRows && cc >= 0 && cc < numCols) {
                let neighbor = nodes[cr][cc];
                node.addNeighbor(neighbor);
              }
            }
          }
        }
      }
    } else {
      throw new Error('Unexpected networkType: ' + networkType);
    }

    return network
  }
}
